import { HTMLAttributes, useCallback, useEffect, useState } from "react";
import {
  fetchUserAttributes,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
  type VerifyTOTPSetupInput,
} from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import QRCode from "qrcode";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { StatusButton } from "@/components/ui/loading-button";
import { LoadingScreen } from "@/components/ui/loading-screen";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

type TOTPSetupFormProps = HTMLAttributes<HTMLDivElement> & {
  isSuperadmin?: boolean;
};

const otpSchema = z.object({
  code: z
    .string()
    .min(6, "TOTP code must be 6 digits")
    .max(6, "TOTP code must be 6 digits"),
});

export function TOTPSetupForm({
  isSuperadmin = false,
  className,
  ...props
}: TOTPSetupFormProps) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof otpSchema>>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      code: "",
    },
  });

  const [qrCodeData, setQrCodeData] = useState<string>("");
  const [secretKey, setSecretKey] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setupTOTP = useCallback(async () => {
    try {
      setLoading(true);
      const user = await fetchUserAttributes();
      const setupDetails = await setUpTOTP();

      const issuer = "Ecosuite";
      const label = encodeURIComponent(user.email || "User");
      const encodedIssuer = encodeURIComponent(issuer);
      const otpauthUrl = `otpauth://totp/${label}?secret=${setupDetails.sharedSecret}&issuer=${encodedIssuer}`;

      const dataUrl = await QRCode.toDataURL(otpauthUrl);

      setQrCodeData(dataUrl);
      setSecretKey(setupDetails.sharedSecret);
    } catch (error) {
      console.error("Error setting up TOTP:", error);
      toast.error(
        "Could not set up TOTP. Please try again or contact support.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void setupTOTP();
  }, [setupTOTP]);

  const onSubmit = async (data: z.infer<typeof otpSchema>) => {
    try {
      setIsSubmitting(true);
      const verifyInput: VerifyTOTPSetupInput = {
        code: data.code.trim(),
      };
      await verifyTOTPSetup(verifyInput);

      await updateMFAPreference({
        totp: "PREFERRED",
      });
      toast.success("TOTP setup successfully!");

      isSuperadmin
        ? navigate({ to: "/root-user/dashboard" })
        : navigate({ to: "/dashboard" });
    } catch (error) {
      toast.error("Invalid code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      {loading ? (
        <LoadingScreen text="Setting up TOTP..." className="h-40" />
      ) : (
        <>
          {qrCodeData && (
            <div className="mb-4 flex flex-col items-center">
              <img
                src={qrCodeData}
                alt="TOTP QR Code"
                className="border-muted h-40 w-40 border p-2"
              />
              <p className="text-muted-foreground mt-2 break-all text-xs">
                Or manually use this secret key: <br />
                <strong>{secretKey}</strong>
              </p>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Enter Authenticator Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="6-digit code"
                        autoComplete="one-time-code"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <StatusButton
                type="submit"
                className="mt-4 w-full"
                isLoading={isSubmitting}
              >
                Verify TOTP Setup
              </StatusButton>
            </form>
          </Form>
        </>
      )}
    </div>
  );
}
